/**
 * Utilidades para detección y manejo de subdominios multi-tenant de catálogos
 * Ejemplo:
 *   - belenshop.mundoxyz.com -> 'belenshop'
 *   - catalogo.mundoxyz.com -> 'default'
 *   - aguamarina.mundoxyz.com -> 'hotel-aguamarina'
 *   - belenshop.localhost:3000 -> 'belenshop' (soporte dev local)
 */

const SYSTEM_SUBDOMAINS = new Set([
  'www',
  'media',
  'cdn',
  'r2',
  'assets',
  'storage',
  's3',
  'api',
  'app',
  'admin',
  'mail',
  'smtp',
  'ftp',
  'test',
  'staging',
  'dev'
]);

const SLUG_ALIASES = {
  'aguamarina': 'hotel-aguamarina',
  'hotelaguamarina': 'hotel-aguamarina',
  'menu-hotel-aguamarina': 'hotel-aguamarina',
  'mahoalbi': 'maho-albi',
  'gaming-spot': 'gamingspot',
  'roeljoyas': 'roel',
  'roel-joyas': 'roel'
};

/**
 * Detecta si el hostname actual corresponde a un subdominio de catálogo
 * @param {string} [hostnameOverride] - Hostname opcional para pruebas
 * @returns {string|null} storeSlug detectado o null si es el dominio principal/sistema
 */
export function detectCatalogSubdomain(hostnameOverride) {
  try {
    const host = (
      hostnameOverride ||
      (typeof window !== 'undefined' ? window.location.hostname : '')
    ).toLowerCase().trim();

    if (!host) return null;

    // Soporte para pruebas en desarrollo vía query param: ?store_subdomain=belenshop
    if (typeof window !== 'undefined' && window.location.search) {
      const sp = new URLSearchParams(window.location.search);
      const testSubdomain = sp.get('store_subdomain') || sp.get('subdomain');
      if (testSubdomain && testSubdomain.trim()) {
        const cleanTest = testSubdomain.trim().toLowerCase();
        return SLUG_ALIASES[cleanTest] || cleanTest;
      }
    }

    // 1. Dominio en producción: *.mundoxyz.com o *.mundoxyz
    if (host.endsWith('.mundoxyz.com') || host.endsWith('.mundoxyz') || host.includes('.mundoxyz.')) {
      const parts = host.split('.');
      if ((host.endsWith('.mundoxyz.com') && parts.length >= 3) || parts.length >= 2) {
        const sub = parts[0];
        if (SYSTEM_SUBDOMAINS.has(sub)) return null;
        if (sub === 'catalogo') return 'default';
        return SLUG_ALIASES[sub] || sub;
      }
      return null;
    }

    // 2. Soporte local para desarrollo: *.localhost (navegadores resuelven *.localhost a 127.0.0.1)
    if (host.endsWith('.localhost')) {
      const parts = host.split('.');
      if (parts.length >= 2) {
        const sub = parts[0];
        if (SYSTEM_SUBDOMAINS.has(sub)) return null;
        if (sub === 'catalogo') return 'default';
        return SLUG_ALIASES[sub] || sub;
      }
    }

    return null;
  } catch (err) {
    console.warn('[detectCatalogSubdomain] Error parsing host:', err);
    return null;
  }
}

/**
 * Retorna la URL base recomendada para un catálogo según el entorno actual
 * @param {string} storeSlug 
 * @returns {string} URL completa
 */
export function getCatalogBaseUrl(storeSlug) {
  const clean = (storeSlug || 'default').toLowerCase().trim();
  const currentSubdomain = detectCatalogSubdomain();

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    // Si ya estamos en el subdominio de la tienda actual
    if (currentSubdomain && currentSubdomain === clean) {
      return origin;
    }

    // Si estamos en producción en mundoxyz.com
    const host = window.location.hostname;
    if (host.includes('mundoxyz.com')) {
      if (clean === 'default') {
        return 'https://catalogo.mundoxyz.com';
      }
      return `https://${clean}.mundoxyz.com`;
    }

    // Si estamos en localhost
    if (host.includes('localhost')) {
      const port = window.location.port ? `:${window.location.port}` : '';
      if (clean === 'default') {
        return `${window.location.protocol}//catalogo.localhost${port}`;
      }
      return `${window.location.protocol}//${clean}.localhost${port}`;
    }
  }

  // Fallback estándar
  return clean === 'default' ? 'https://mundoxyz.com/catalogo' : `https://mundoxyz.com/c/${clean}`;
}

/**
 * Retorna la URL del asistente para crear catálogos (Wizard).
 * Si el usuario está navegando en un subdominio multi-tenant (*.mundoxyz.com o *.localhost),
 * la redirección debe apuntar al dominio raíz de la plataforma (https://mundoxyz.com/crear-catalogo)
 * para garantizar la correcta gestión de sesiones, registro y login centralizado.
 */
export function getCatalogCreationWizardUrl() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // Si estamos en un subdominio en producción (*.mundoxyz.com pero no mundoxyz.com ni www.mundoxyz.com)
    if (host.endsWith('.mundoxyz.com') && host !== 'mundoxyz.com' && host !== 'www.mundoxyz.com') {
      return 'https://mundoxyz.com/crear-catalogo';
    }
    // Si estamos en desarrollo en un subdominio (*.localhost)
    if (host.endsWith('.localhost') && host !== 'localhost') {
      const port = window.location.port ? `:${window.location.port}` : '';
      return `${window.location.protocol}//localhost${port}/crear-catalogo`;
    }
  }
  return '/crear-catalogo';
}

/**
 * Navega de forma segura al wizard para crear catálogo según el entorno.
 * Si es una URL externa/absoluta, hace redirección de ventana. Si es interna, usa navigate.
 * @param {Function} [navigate] - Función de react-router-dom
 */
export function navigateToCatalogCreationWizard(navigate) {
  const url = getCatalogCreationWizardUrl();
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.location.href = url;
  } else if (typeof navigate === 'function') {
    navigate(url);
  } else if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

/**
 * Retorna la URL absoluta apuntando al dominio raíz de la plataforma principal (MundoXYZ),
 * escapando de cualquier subdominio (*.mundoxyz.com o *.localhost) para acceder al panel de tienda,
 * dashboard o rutas globales sin colisiones de enrutamiento ni pérdida de sesiones.
 * @param {string} [path='/']
 * @returns {string} URL absoluta
 */
export function getMainPlatformUrl(path = '/') {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // En producción: subdominio *.mundoxyz.com o *.mundoxyz (ej. roel.mundoxyz.com) -> https://mundoxyz.com/store/roel/dashboard
    if ((host.endsWith('.mundoxyz.com') || host.endsWith('.mundoxyz')) && host !== 'mundoxyz.com' && host !== 'www.mundoxyz.com' && host !== 'mundoxyz') {
      return `https://mundoxyz.com${cleanPath}`;
    }
    // En desarrollo local: subdominio *.localhost (ej. roel.localhost:3001) -> http://localhost:3001/store/roel/dashboard
    if (host.endsWith('.localhost') && host !== 'localhost') {
      const port = window.location.port ? `:${window.location.port}` : '';
      return `${window.location.protocol}//localhost${port}${cleanPath}`;
    }
  }
  return cleanPath;
}

/**
 * Retorna la URL para acceder a la administración del catálogo de una tienda específica
 * @param {string} storeSlug 
 * @returns {string} URL absoluta
 */
export function getCatalogAdminUrl(storeSlug, preferPlatform = true) {
  const clean = (storeSlug || 'default').toLowerCase().trim();
  if (preferPlatform && typeof window !== 'undefined') {
    const currentSubdomain = detectCatalogSubdomain();
    if (!currentSubdomain) {
      const origin = window.location.origin;
      return clean === 'default' ? `${origin}/catalogo/admin` : `${origin}/c/${clean}/admin`;
    }
  }
  const baseUrl = getCatalogBaseUrl(storeSlug);
  return `${baseUrl.replace(/\/+$/, '')}/admin`;
}
